/** Seeded Poisson-disc placement of systems in concentric regional bands (M3 spec §3). Load-time code. */
import { logger } from '../core/logger'
import { createRng, nextFloat, range } from '../core/rng'
import type { GalaxyConfig } from '../data/galaxyConfig'
import { deriveSeed } from './seed'
import type { RegionId } from './types'

export interface PlacedSystem {
  region: RegionId
  mapPos: [number, number, number]
}

/** Logger message emitted (with `{ region, spacing }`) whenever a band's spacing is relaxed. */
export const PLACEMENT_RELAXED = 'galaxy: relaxed placement spacing'

/** The home system: the gate-region system nearest the origin (the ancient gate). */
export function homeIndex(placed: readonly PlacedSystem[]): number {
  let best = -1
  let bestR = Infinity
  placed.forEach((p, i) => {
    const r = Math.hypot(p.mapPos[0], p.mapPos[1])
    if (p.region === 'gate' && r < bestR) {
      best = i
      bestR = r
    }
  })
  return best
}

export function systemId(index: number): string {
  return `sys-${String(index).padStart(2, '0')}`
}

/**
 * Places every region's systems from the inside out. Candidates are uniform in area within the band, with a z
 * jitter; a candidate is kept when it is far enough (measured in x, y) from every system placed so far: at least
 * the looser of the two systems' spacings. Each region may set its own `minSpacing` (D-037). A region with
 * `angularJitter` is spread around its ring, one system per equal sector (with a seeded offset), inside that
 * central fraction of the sector. After `maxTries` consecutive rejections the band's spacing shrinks by `relaxFactor`.
 */
export function placeSystems(seed: number, cfg: GalaxyConfig): PlacedSystem[] {
  const rng = createRng(deriveSeed(seed, 'placement'))
  const { zJitter, minSpacing, maxTries, relaxFactor } = cfg.placement
  const placed: PlacedSystem[] = []
  const spacingAt: number[] = [] // the spacing in force when each system was placed
  for (const band of cfg.regions) {
    let spacing = band.minSpacing ?? minSpacing
    let fails = 0
    let inBand = 0
    const r2Min = band.rMin * band.rMin
    const r2Max = band.rMax * band.rMax
    const jitter = band.angularJitter
    const sector = (Math.PI * 2) / band.count
    const offset = jitter === undefined ? 0 : range(rng, 0, Math.PI * 2)
    while (inBand < band.count) {
      const angle =
        jitter === undefined ? range(rng, 0, Math.PI * 2) : offset + (inBand + 0.5 + (nextFloat(rng) - 0.5) * jitter) * sector
      const r = Math.sqrt(range(rng, r2Min, r2Max))
      const x = r * Math.cos(angle)
      const y = r * Math.sin(angle)
      const z = range(rng, -zJitter, zJitter)
      if (isClear(placed, spacingAt, x, y, spacing)) {
        placed.push({ region: band.id, mapPos: [x, y, z] })
        spacingAt.push(spacing)
        inBand++
        fails = 0
      } else if (++fails >= maxTries) {
        spacing *= relaxFactor
        fails = 0
        logger.warn(PLACEMENT_RELAXED, { region: band.id, spacing })
      }
    }
  }
  return placed
}

function isClear(placed: readonly PlacedSystem[], spacingAt: readonly number[], x: number, y: number, spacing: number): boolean {
  for (let i = 0; i < placed.length; i++) {
    const need = Math.min(spacing, spacingAt[i])
    const dx = placed[i].mapPos[0] - x
    const dy = placed[i].mapPos[1] - y
    if (dx * dx + dy * dy < need * need) return false
  }
  return true
}
